import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { authApi } from '../lib/api';

export type CanonicalUserRole = 'admin' | 'manager' | 'operator' | 'viewer';

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: CanonicalUserRole;
  avatarUrl?: string;
  permissions: string[];
}

interface UserRow {
  id: string;
  auth_user_id: string | null;
  email: string | null;
  full_name: string | null;
  role: CanonicalUserRole;
  avatar_url: string | null;
  is_active: boolean | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  loginWithSupabase: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  checkAuth: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function isCanonicalRole(value: unknown): value is CanonicalUserRole {
  return value === 'admin' || value === 'manager' || value === 'operator' || value === 'viewer';
}

function getRolePermissions(role: CanonicalUserRole): string[] {
  const readOnly = [
    'read:orders',
    'read:inventory',
    'read:products',
    'read:warehouses',
    'read:deliveries',
    'read:reports',
    'read:qc',
    'read:recalls',
    'read:dock',
    'read:fleet',
  ];

  if (role === 'admin') {
    return [
      'read:all',
      'write:all',
      'delete:all',
      'admin:system',
      'admin:users',
      'admin:warehouses',
      ...readOnly,
      'read:users',
    ];
  }

  if (role === 'manager') {
    return [
      ...readOnly,
      'read:users',
      'write:orders',
      'write:inventory',
      'write:products',
      'write:warehouses',
      'write:deliveries',
      'write:labels',
      'write:qc',
      'write:recalls',
      'write:dock',
      'write:fleet',
      'verify:orders',
      'pick:orders',
      'check:orders',
      'wrap:orders',
      'stage:orders',
      'deliver:orders',
      'move:pallets',
      'receive:inventory',
      'qc:hold',
      'qc:release',
      'approve:amendments',
      'initiate:recall',
      'manage:dock',
      'manage:fleet',
      'export:reports',
    ];
  }

  if (role === 'operator') {
    return [
      ...readOnly,
      'write:orders',
      'write:inventory',
      'write:deliveries',
      'write:labels',
      'write:qc',
      'write:dock',
      'write:fleet',
      'verify:orders',
      'pick:orders',
      'check:orders',
      'wrap:orders',
      'stage:orders',
      'deliver:orders',
      'move:pallets',
      'receive:inventory',
      'qc:hold',
      'qc:release',
    ];
  }

  return readOnly;
}

function mapProfile(authUserId: string, authEmail: string | undefined, profile: UserRow): User {
  if (!isCanonicalRole(profile.role)) {
    throw new Error('User account role is invalid');
  }
  if (profile.is_active === false) {
    throw new Error('User account is deactivated');
  }

  return {
    id: authUserId,
    email: profile.email || authEmail || '',
    fullName: profile.full_name || profile.email || authEmail || 'User',
    role: profile.role,
    avatarUrl: profile.avatar_url ?? undefined,
    permissions: getRolePermissions(profile.role),
  };
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authApi.login(email, password);
          const { token, user } = response.data.data;
          localStorage.setItem('auth_token', token);
          set({
            user: user as User,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ user: null, token: null, isAuthenticated: false, isLoading: false, error: message });
          throw error;
        }
      },

      loginWithSupabase: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) throw error;
          if (!data.session || !data.user) throw new Error('Authentication session was not created');

          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id,auth_user_id,email,full_name,role,avatar_url,is_active')
            .eq('auth_user_id', data.user.id)
            .maybeSingle();
          if (userError || !userData) throw userError || new Error('User account is not provisioned');

          const user = mapProfile(
            data.user.id,
            data.user.email,
            userData as unknown as UserRow
          );

          localStorage.setItem('auth_token', data.session.access_token);
          set({
            user,
            token: data.session.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ user: null, token: null, isAuthenticated: false, isLoading: false, error: message });
          throw error;
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true });
          try {
            await authApi.logout();
          } catch {
            // API logout is best-effort; local/Supabase session clearing still runs.
          }
          await supabase.auth.signOut();
        } finally {
          localStorage.removeItem('auth_token');
          set({ ...initialState, isLoading: false });
        }
      },

      refreshUser: async () => {
        try {
          if (!get().token) return;
          const response = await authApi.me();
          const user = response.data.data as User;
          set({ user });
        } catch {
          await get().logout();
        }
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true });
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session?.user) {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('id,auth_user_id,email,full_name,role,avatar_url,is_active')
              .eq('auth_user_id', session.user.id)
              .maybeSingle();

            if (!userError && userData) {
              const user = mapProfile(
                session.user.id,
                session.user.email,
                userData as unknown as UserRow
              );
              localStorage.setItem('auth_token', session.access_token);
              set({ user, token: session.access_token, isAuthenticated: true, isLoading: false, error: null });
              return;
            }
          }

          const token = localStorage.getItem('auth_token');
          if (token) {
            try {
              const response = await authApi.me();
              const user = response.data.data as User;
              set({ user, token, isAuthenticated: true, isLoading: false, error: null });
              return;
            } catch {
              localStorage.removeItem('auth_token');
            }
          }

          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'warehouse-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
